import type { CanonicalEntry } from "./canonical-entry.js";

/**
 * What one session remembers about the words it has been asked for.
 *
 * Apart from `glosses.ts` because this answers a different question. That file
 * decides where an answer comes from; this one decides what is worth asking
 * for twice. A background worker is torn down after about thirty seconds of
 * inactivity and a tools page lives as long as the tab, so everything here
 * lasts one sitting at most and `gloss-store.ts` is what outlives it.
 *
 * **What is kept is the asking rather than the answer.** Two selections in
 * flight at once overlap in the commonest words, and a map of resolved entries
 * would have both of them fetching 的 because neither had finished. A map of
 * promises answers the second from the first's request. The segmenter asks
 * about a few hundred words at once and meets the same overlap.
 *
 * **The answers are kept beside them, for the question `held` asks.** The
 * panel is drawn before any request has been made, so it needs what is known
 * this instant rather than a promise of it. A second selection over the same
 * paragraph therefore draws complete, and only a word genuinely new to this
 * sitting arrives late.
 *
 * Neither map is evicted. A session holding a few thousand of these is holding
 * a few hundred kilobytes, which is a shorter life than any policy here.
 */
export class GlossMemory {
  readonly #asked = new Map<string, Promise<CanonicalEntry | undefined>>();
  readonly #answered = new Map<string, CanonicalEntry>();

  /**
   * The asking already under way for a word, or one started now.
   *
   * **An asking that fails is dropped and one that answers is not.** A 404 is
   * the site saying there is no entry, and it will say the same tomorrow. A
   * dropped connection is the network saying nothing at all, and remembering
   * it would leave the word permanently blank in a session that has since
   * recovered.
   *
   * The catch is hung on the promise out here rather than put inside `ask`, so
   * that the forgetting is always a later microtask than the `set` below. An
   * `ask` throwing where it stands would otherwise delete an entry not yet
   * written.
   */
  asking(
    word: string,
    ask: () => Promise<CanonicalEntry | undefined>,
  ): Promise<CanonicalEntry | undefined> {
    const already = this.#asked.get(word);

    if (already !== undefined) {
      return already;
    }

    const asking = ask().catch(() => this.#forget(word));

    this.#asked.set(word, asking);

    return asking;
  }

  /**
   * The entries among these words this session can answer for without asking.
   *
   * Synchronous, which is the point. It is what the panel is drawn from before
   * a single request has gone out, so it cannot be a promise of anything.
   */
  held(words: readonly string[]): ReadonlyMap<string, CanonicalEntry> {
    const found = new Map<string, CanonicalEntry>();

    for (const word of words) {
      const entry = this.#answered.get(word);

      if (entry !== undefined) {
        found.set(word, entry);
      }
    }

    return found;
  }

  /** Write an answer down where {@link held} will find it, and hand it back. */
  recall(word: string, entry: CanonicalEntry | undefined): typeof entry {
    if (entry !== undefined) {
      this.#answered.set(word, entry);
    }

    return entry;
  }

  /** Stop holding an asking, so that the next selection starts a new one. */
  #forget(word: string): undefined {
    this.#asked.delete(word);

    return undefined;
  }
}
